<template>
  <div>
    <v-form ref="form" @submit.prevent="submitForm">
      <v-stepper v-model="currentStep">
        <v-stepper-header>
          <v-stepper-step :complete="currentStep > 1" step="1">
            Basic information
          </v-stepper-step>

          <v-divider></v-divider>

          <v-stepper-step :complete="currentStep > 2" step="2">
            Detailed information
          </v-stepper-step>

          <v-divider></v-divider>

          <v-stepper-step :complete="currentStep > 3" step="3">
            Address information
          </v-stepper-step>
        </v-stepper-header>

        <v-stepper-items>
          <v-stepper-content step="1" class="stepper-content">
            <v-row class="pt-5">
              <v-col cols="12" sm="5">
                <v-select
                  v-model="selectedFunitType"
                  :items="funitTypeItems"
                  label="Functional unit type"
                  item-text="label"
                  item-value="name"
                  :rules="[required]"
                  outlined
                  required
                  dense
                ></v-select>
              </v-col>
              <v-col sm="2" class="d-flex justify-center">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      outlined
                      width="20px"
                      @click="addParentDialog = true"
                      v-bind="attrs"
                      v-on="on"
                      :disabled="addParentDisabled"
                      :color="addParentBtnColor"
                      ><v-icon>domain_add</v-icon></v-btn
                    >
                  </template>
                  <span>Select parent building</span>
                </v-tooltip>
              </v-col>
              <v-col cols="12" sm="5">
                <v-select
                  v-model="selectedBuildingTypeUsage"
                  :items="buildingTypeUsageItems"
                  label="Building type usage"
                  item-text="label"
                  item-value="name"
                  :rules="[required]"
                  outlined
                  required
                  dense
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" sm="6" md="4">
                <v-select
                  v-model="year_of_construction"
                  :items="yearOptions"
                  label="Construction year"
                  outlined
                  dense
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <v-select
                  v-model="renovation_year"
                  :items="yearOptions"
                  label="Latest renovation year"
                  :rules="[higherThanConstructionYear]"
                  outlined
                  dense
                  clearable
                ></v-select>
              </v-col>

              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  v-model.number="floor_area"
                  label="Floor area(*)"
                  type="number"
                  :rules="[nonNegative, required, numeric]"
                  outlined
                  dense
                >
                  <template slot="append"
                    ><span style="display: block; margin-top: 4px; color: gray"
                      >m<sup>2</sup></span
                    ></template
                  >
                </v-text-field>
              </v-col>
            </v-row>

            <v-btn color="primary" @click="currentStep = 2"> Continue </v-btn>
          </v-stepper-content>

          <v-stepper-content step="2" class="stepper-content">
            <v-row class="pt-5">
              <v-col :cols="nameCol">
                <v-text-field
                  v-model="name"
                  label="Name"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col v-if="!floorDisabled" :cols="floorCol">
                <v-text-field
                  v-model="floor"
                  label="Floor number(*)"
                  outlined
                  dense
                  v-if="!floorDisabled"
                  :rules="[required]"
                ></v-text-field>
              </v-col>
              <v-col v-if="!doorNumberDisabled" :cols="doorCol">
                <v-text-field
                  v-model="door_number"
                  label="Door number(*)"
                  type="number"
                  outlined
                  dense
                  v-if="!doorNumberDisabled"
                  :rules="[required]"
                ></v-text-field>
              </v-col>
              <v-col v-if="!roomDisabled" :cols="roomCol">
                <v-text-field
                  v-model="room"
                  label="Room(*)"
                  outlined
                  dense
                  v-if="!roomDisabled"
                  :rules="[required]"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-textarea
                  v-model="description"
                  label="Description"
                  placeholder="Enter a description"
                  rows="1"
                  outlined
                  dense
                ></v-textarea>
              </v-col>
            </v-row>
            <v-btn color="primary" class="mr-5" @click="currentStep = 3">
              Continue
            </v-btn>
            <v-btn color="primary" @click="currentStep = 1"> Back </v-btn>
          </v-stepper-content>

          <v-stepper-content step="3" class="stepper-content">
            <v-row class="pt-5">
              <v-col cols="12" sm="4" md="4">
                <v-select
                  v-model="country"
                  :items="countryItems"
                  label="Country(*)"
                  item-text="label"
                  item-value="name"
                  :disabled="!!parent_id"
                  outlined
                  dense
                  required
                  :rules="[required]"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="4" md="4">
                <v-text-field
                  v-model="city"
                  label="City(*)"
                  :rules="[required]"
                  :disabled="!!parent_id"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4" md="4">
                <v-text-field
                  v-model="postal_code"
                  label="Postal Code(*)"
                  :rules="[required, postalCodeStructureRule]"
                  :disabled="!!parent_id"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="6" md="4" lg="4" xl="4">
                <v-text-field
                  v-model="street"
                  label="Street name(*)"
                  :rules="[required]"
                  :disabled="!!parent_id"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="2" lg="2" xl="2">
                <v-text-field
                  v-model="street_number"
                  label="Street Number(*)"
                  :rules="[required]"
                  :disabled="!!parent_id"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="3" lg="3" xl="3">
                <v-text-field
                  v-model.number="centroid_lat"
                  label="Latitude"
                  type="number"
                  :rules="[numeric, centroidLatitudeRules]"
                  :disabled="!!parent_id"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="3" lg="3" xl="3">
                <v-text-field
                  v-model.number="centroid_long"
                  label="Longitude"
                  type="number"
                  :rules="[numeric, centroidLongitudeRules]"
                  :disabled="!!parent_id"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
            </v-row>
            <div class="d-flex align-top justify-space-between">
              <div>
                <v-btn type="submit" class="white--text mr-4" color="success"
                  >Complete</v-btn
                >
                <v-btn class="mr-4" color="primary" @click="currentStep = 2">
                  Back
                </v-btn>
                <v-tooltip top v-if="!parent_id">
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      x-large
                      color="primary"
                      class="white--text"
                      @click="selectFromMapDialog = true"
                      v-on="on"
                      ><v-icon>mdi-earth</v-icon>
                    </v-btn>
                  </template>
                  <span>Select from map</span>
                </v-tooltip>
                <v-tooltip top v-if="!!parent_id">
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      x-large
                      color="orange"
                      class="white--text"
                      v-on="on"
                      ><v-icon>mdi-alert</v-icon>
                    </v-btn>
                  </template>
                  <span
                    >Address data is from parent <br />building. Cannot be
                    edited.</span
                  >
                </v-tooltip>
              </div>

              <div>
                <v-checkbox
                  class="mt-0"
                  v-model="certifiable"
                  label="Certifiable"
                ></v-checkbox>
              </div>
            </div>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>
    </v-form>
    <v-dialog v-model="addParentDialog" persistent max-width="900px">
      <v-card min-height="450px">
        <v-toolbar color="primary" dense dark
          ><v-toolbar-title>Select parent building</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="addParentDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn></v-toolbar
        >
        <v-card-text class="pt-10">
          <BuildingParentSelection
            @addParent="handleAddParent"
            :parentId="parent_id"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="selectFromMapDialog"
      persistent
      width="80%"
      height="500px"
    >
      <v-card>
        <v-toolbar color="primary" dense dark
          ><v-toolbar-title>Select address</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="selectFromMapDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn></v-toolbar
        >
        <v-card-text class="pt-4">
          <geo-search-map
            :latitudeParent="centroid_lat"
            :longitudeParent="centroid_long"
            :isOpen="selectFromMapDialog"
            @savePosition="savePosition"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
    <div>
      <v-snackbar timeout="2000" v-model="snackbarValue" absolute color="error">
        <div class="text-center">
          <span>{{ snackbarMessage }}</span>
        </div>
      </v-snackbar>
    </div>
  </div>
</template>

<script>
import services from "../rest/Services";
import utility from "../utility/utility";
import BuildingParentSelection from "../components/BuildingParentSelection.vue";
import EventBus from "../components/EventBus";
import GeoSearchMap from "./GeoSearchMap.vue";
export default {
  props: {
    buildingData: {
      type: Object,
      default: null,
    },
    buildingId: {
      type: Number,
      default: null,
    },
  },
  components: {
    BuildingParentSelection,
    GeoSearchMap,
  },
  data() {
    return {
      selectFromMapDisabled: false,
      selectFromMapDialog: false,
      currentStep: 1,
      id: null,
      parent_id: null,
      selectedFunitType: "BUILDING",
      selectedBuildingTypeUsage: "SINGLE_FAMILY_HOUSE",
      year_of_construction: 2020,
      renovation_year: null,
      floor_area: 0,
      floor: null,
      door_number: null,
      room: null,
      name: null,
      description: null,
      certifiable: false,
      centroid_lat: 0,
      centroid_long: 0,

      floorDisabled: true,
      doorNumberDisabled: true,
      roomDisabled: true,

      //address
      country: null,
      city: null,
      postal_code: null,
      street: null,
      street_number: null,

      funitTypeItems: [],
      buildingTypeUsageItems: [],
      countryItems: [],
      yearOptions: [],
      nonNegative: (v) => v > 0 || "Please enter a positive number",
      centroidLatitudeRules: (v) =>
        (v >= -90 && v <= 90) || "Centroid latitude must be between -90 and 90",
      centroidLongitudeRules: (v) =>
        (v >= -180 && v <= 180) ||
        "Centroid longitude must be between -180 and 180",
      required: (v) => !!v || "This field is required",
      higherThanConstructionYear: (v) => {
        if (parseInt(v) && v < this.year_of_construction)
          return "Renovation year must be higher than construction year";
        return true;
      },
      numeric: (v) => Number.isFinite(v) || "Please provide a number",
      postalCodeStructureRule: (v) =>
        /^[A-Za-z0-9\s]*$/.test(v) ||
        "Postal Code can only contain letters, numbers, and spaces",
      snackbarValue: false,
      snackbarMessage: "",
      addParentDialog: false,
      roomCol: 0,
      doorCol: 0,
      floorCol: 0,
      nameCol: 12,
      codebooks: null,
    };
  },
  mounted: function () {
    this.setCodebookData();
    this.setYearOptions();
    EventBus.$on("logout", () => {
      this.resetForm();
    });
  },
  computed: {
    addParentDisabled: function () {
      return this.selectedFunitType === "BUILDING" ? true : false;
    },
    addParentBtnColor: function () {
      return this.parent_id ? "green" : "#777777";
    },
  },

  methods: {
    resetForm() {
      this.$refs.form.reset();
      EventBus.$emit("resetParentSelection");
    },
    submitForm() {
      if (!this.$refs.form.validate()) {
        this.snackbarMessage =
          "Please fill out all the required fields indicated by an asterisk (*)  ";
        this.snackbarValue = true;
        if (!this.floor_area) this.currentStep = 1;
        return;
      }
      const data = {
        id: this.id,
        parent_id: this.parent_id,
        functional_unit_type: this.selectedFunitType,
        building_type_usage: this.selectedBuildingTypeUsage,
        year_of_construction: parseInt(this.year_of_construction),
        floor_area: this.floor_area,
        renovation_year: this.renovation_year
          ? parseInt(this.renovation_year)
          : null,
        floor: this.floorDisabled ? null : this.floor,
        door_number: this.doorNumberDisabled ? null : this.door_number,
        room: this.roomDisabled ? null : this.room,
        name: this.name ? this.name : null,
        description: this.description ? this.description : null,
        certifiable: this.certifiable ? true : false,
        address_data: {
          country: this.country,
          city: this.city,
          postal_code: this.postal_code,
          street: this.street,
          street_number: this.street_number,
        },
        centroid_lat: this.centroid_lat,
        centroid_long: this.centroid_long,
      };
      services
        .saveOrUpdateFunctionalUnit(data)
        .then((response) => {
          this.$emit("saveSuccess");
          EventBus.$emit("saveBuildingSuccess");
          this.resetForm();
          this.parent_id = null;
          this.currentStep = 1;
          console.log(response);
        })
        .catch((error) => {
          let message;
          if (error.response.status === 409) {
            message =
              "The data you provided matches an existing functional unit in our system. Please review your submission and ensure that the information is accurate and unique. ";
          } else message = "Error while trying to save functional unit";
          this.snackbarValue = true;
          this.snackbarMessage = message;
        });
    },
    setYearOptions: function () {
      // generate options for years starting from current year - 100 years
      const currentYear = new Date().getFullYear();
      for (let i = currentYear; i >= currentYear - 100; i--) {
        this.yearOptions.push(i);
      }
    },
    setCodebookData: function () {
      const codebooks = JSON.parse(
        sessionStorage.getItem("dbData")
      ).enum_constants_lookup;
      this.countryItems = this.getTable("country", codebooks);
      this.buildingTypeUsageItems = this.getTable(
        "building_type_usage",
        codebooks
      );
      this.funitTypeItems = this.getTable("functional_unit_type", codebooks);
    },
    getTable: (table, codebooks) => {
      return codebooks[table].map((item, index) => {
        return {
          id: index,
          name: item,
          label: utility.convertStrToLabel(item),
        };
      });
    },
    setFormForEdit: function (data) {
      const fu = data.functional_unit;
      this.id = fu.id;
      this.parent_id = fu.parent_id;
      this.selectedFunitType = fu.functional_unit_type;
      this.selectedBuildingTypeUsage = fu.building_type_usage;
      this.year_of_construction = fu.year_of_construction;
      this.floor_area = fu.floor_area;
      this.renovation_year = parseInt(fu.renovation_year);
      this.floor = fu.floor;
      this.door_number = fu.door_number;
      this.room = fu.room;
      this.name = fu.name;
      this.description = fu.description;
      this.certifiable = fu.certifiable ? true : false;

      this.setAddress(fu);
    },
    setAddress: function (fu) {
      this.country = fu ? fu.address_data.country : null;
      this.city = fu ? fu.address_data.city : null;
      this.postal_code = fu ? fu.address_data.postal_code : null;
      this.street = fu ? fu.address_data.street : null;
      this.street_number = fu ? fu.address_data.street_number : null;
      if (fu) {
        this.centroid_lat = fu.centroid_lat || null;
        this.centroid_long = fu.centroid_long || null;
      }
    },
    setFromParent: function (fu) {
      this.selectedBuildingTypeUsage = fu ? fu.building_type_usage : null;
      this.year_of_construction = fu ? parseInt(fu.year_of_construction) : null;
      this.renovation_year = fu ? parseInt(fu.renovation_year) : null;
    },
    savePosition(position, geocoding) {
      this.centroid_lat = position[0];
      this.centroid_long = position[1];
      this.country = geocoding.country || null;
      this.city = geocoding.city || null;
      this.street = geocoding.street || null;
      this.street_number = geocoding.housenumber || null;
      this.postal_code =
        geocoding.postcode || this.extractPostalCode(geocoding.label);
      this.selectFromMapDialog = false;
    },
    extractPostalCode(label) {
      const props = label.split(",");
      const postalCode = props[props.length - 2];
      return parseInt(postalCode) ? parseInt(postalCode) : null;
    },

    handleCloseDialog: function () {
      this.$emit("closeDialog");
    },
    handleAddParent: function (parent) {
      this.setAddress(parent);
      this.setFromParent(parent);
      this.parent_id = parent ? parent.id : null;
      this.addParentDialog = false;
      //this.selectFromMapDisabled = parent ? true : false;
    },
  },
  watch: {
    buildingData: function (value) {
      if (value && Object.keys(value).length > 0) {
        this.setFormForEdit(this.buildingData);
      } else {
        this.$refs.form.reset();
      }
    },
    selectedFunitType: function (value) {
      switch (value) {
        case "BUILDING":
          this.floorDisabled = true;
          this.roomDisabled = true;
          this.doorNumberDisabled = true;
          this.nameCol = 12;
          EventBus.$emit("resetParentSelection");
          break;
        case "APARTMENT":
          this.floorDisabled = false;
          this.roomDisabled = true;
          this.doorNumberDisabled = false;
          this.nameCol = 4;
          this.doorCol = 4;
          this.floorCol = 4;
          break;
        case "FLOOR":
          this.floorDisabled = false;
          this.roomDisabled = true;
          this.doorNumberDisabled = true;
          this.nameCol = 6;
          this.floorCol = 6;
          break;
        case "ROOM":
          this.floorDisabled = false;
          this.roomDisabled = false;
          this.doorNumberDisabled = false;
          this.nameCol = 3;
          this.roomCol = 3;
          this.floorCol = 3;
          this.doorCol = 3;
          break;
        default:
          break;
      }
    },
  },
};
</script>

<style>
.stepper-content {
  min-height: 270px;
}
</style>
